import { motion } from "framer-motion";
import { ScanLine, Sparkles, Timer, Volume2 } from "lucide-react";

const steps = [
  {
    icon: ScanLine,
    step: "01",
    title: "Escanea el QR",
    desc: "Cada vela tiene un codigo QR unico en su etiqueta.",
  },
  {
    icon: Sparkles,
    step: "02",
    title: "Ritual Personalizado",
    desc: "Se abre tu experiencia digital adaptada al aroma y mood.",
  },
  {
    icon: Timer,
    step: "03",
    title: "Temporizador",
    desc: "Elige micro-rituales de 3, 5, 10 o 15 minutos.",
  },
  {
    icon: Volume2,
    step: "04",
    title: "Audio Ambiental",
    desc: "Sumérgete con sonidos diseñados para tu momento.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function PhygitalSection() {
  return (
    <section
      id="experiencia"
      data-testid="phygital-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle background glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/3 rounded-full blur-[150px]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          {/* Content */}
          <motion.div
            className="md:col-span-7 md:pr-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          >
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]/80 mb-4">
              Experiencia Phygital
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-serif text-4xl md:text-5xl font-normal tracking-tight leading-tight mb-6 text-white"
              data-testid="phygital-heading"
            >
              Del QR a tu
              <br />
              <span className="italic text-[#D4AF37]">ritual digital</span>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="text-base md:text-lg text-white/50 leading-relaxed mb-12 max-w-lg"
            >
              Cada vela es un puente entre lo fisico y lo digital. Escanea,
              respira y deja que la experiencia personalizada te guie hacia tu
              momento de calma.
            </motion.p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {steps.map((s, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] p-6 group hover:border-[#D4AF37]/20 transition-all duration-500"
                  data-testid={`phygital-step-${i}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <s.icon size={18} className="text-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]/60 font-mono">
                      Paso {s.step}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-1">
                    {s.title}
                  </h3>
                  <p className="text-xs text-white/40 leading-relaxed">
                    {s.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            className="md:col-span-5"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D4AF37]/5 blur-3xl" />
              <img
                src="https://images.pexels.com/photos/11341268/pexels-photo-11341268.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Experiencia phygital Serena"
                className="relative w-full aspect-[3/4] object-cover"
                data-testid="phygital-image"
              />
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#050505] to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
